// settings/pages/NumberSequencesPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
 Hash, Plus, Save, ArrowLeft, Copy, RefreshCw, Clock
} from 'lucide-react';
import { motion } from 'framer-motion';

interface NumberSequence {
 id: string;
 name: string;
 code: string;
 prefix: string | null;
 suffix: string | null;
 nextNumber: number;
 padding: number;
 resetFrequency: string;
 isActive: boolean;
}

export function NumberSequencesPage() {
 const navigate = useNavigate();
 const user = useAuthStore((s) => s.user);
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);
 const [showForm, setShowForm] = useState(false);
 const [form, setForm] = useState({ name: '', code: '', prefix: '', suffix: '', padding: '5', resetFrequency: 'never', nextNumber: '1' });

 const { data: sequences, isLoading } = useQuery<NumberSequence[]>({
 queryKey: ['number-sequences', user?.tenantId],
 queryFn: () => api.getNumberSequences(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const saveMutation = useMutation({
 mutationFn: (payload: any) => api.createNumberSequence(user!.tenantId, payload),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['number-sequences'] });
 addToast({ type: 'success', title: 'Sequence created' });
 setShowForm(false);
 setForm({ name: '', code: '', prefix: '', suffix: '', padding: '5', resetFrequency: 'never', nextNumber: '1' });
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 });

 const generatePreview = () => {
 const num = String(parseInt(form.nextNumber) || 1).padStart(parseInt(form.padding) || 1, '0');
 return `${form.prefix || ''}${num}${form.suffix || ''}`;
 };

 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-[60vh]">
 <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
 className="w-14 h-14 rounded-2xl border-4 border-emerald-200 border-t-emerald-600" />
 </div>
 );
 }

 const activeSequences = (sequences || []).filter(s => s.isActive).length;
 const dailyReset = (sequences || []).filter(s => s.resetFrequency === 'daily').length;

 return (
 <div className="min-h-screen">
 <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

 <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
 <button onClick={() => navigate('/settings')}
 className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 transition-colors mb-3 group">
 <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
 Back to Settings
 </button>
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div>
 <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
 #️⃣ Number Sequences
 </h1>
 <p className="text-slate-500 text-sm mt-1">
 {sequences?.length || 0} sequence{(sequences?.length || 0) !== 1 ? 's' : ''} · {activeSequences} active · {dailyReset} daily reset
 </p>
 </div>
 <Button size="md" leftIcon={<Plus size={16} />} onClick={() => setShowForm(true)}>
 Add Sequence
 </Button>
 </div>
 </motion.div>

 <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
 {[
 { label: 'Total Sequences', value: sequences?.length || 0, gradient: 'from-slate-500 to-slate-700' },
 { label: 'Active', value: activeSequences, gradient: 'from-emerald-500 to-teal-600' },
 { label: 'Daily Reset', value: dailyReset, gradient: 'from-blue-500 to-indigo-600' },
 ].map((stat, i) => (
 <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
 className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
 <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
 <Hash className="w-4 h-4 text-white" />
 </div>
 <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
 <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
 </motion.div>
 ))}
 </div>

 {!sequences?.length ? (
 <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
 className="text-center py-20 bg-white rounded-3xl border border-slate-200 ">
 <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
 <Hash className="w-10 h-10 text-slate-300" />
 </div>
 <h3 className="text-xl font-bold text-slate-700 ">No sequences configured</h3>
 <p className="text-slate-400 mt-2 max-w-md mx-auto">Create auto-numbering rules for invoices, orders, receipts and more.</p>
 <button onClick={() => setShowForm(true)}
 className="mt-6 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 inline-flex items-center gap-2">
 <Plus className="w-5 h-5" /> Create Sequence
 </button>
 </motion.div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {(sequences || []).map((seq, i) => {
 const preview = `${seq.prefix || ''}${String(seq.nextNumber).padStart(seq.padding, '0')}${seq.suffix || ''}`;
 return (
 <motion.div key={seq.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
 whileHover={{ y: -3 }}
 className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
 <div className="p-5">
 <div className="flex items-start justify-between mb-4">
 <div>
 <h3 className="font-semibold text-slate-900 ">{seq.name}</h3>
 <code className="text-xs text-slate-400 font-mono">{seq.code}</code>
 </div>
 <Badge variant={seq.isActive ? 'success' : 'danger'} size="sm">{seq.isActive ? 'Active' : 'Inactive'}</Badge>
 </div>
 <div className="p-4 bg-slate-50 rounded-xl text-center mb-3">
 <p className="text-2xl font-bold font-mono text-slate-900 tracking-wider">{preview}</p>
 </div>
 <div className="flex items-center justify-between text-xs text-slate-500">
 <div className="flex items-center gap-1"><Clock size={12} /><span className="capitalize">{seq.resetFrequency}</span></div>
 <span>Next: {seq.nextNumber}</span>
 </div>
 <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100 ">
 <Button variant="ghost" size="xs" leftIcon={<Copy size={12} />}
 onClick={() => { navigator.clipboard.writeText(preview); addToast({ type: 'success', title: 'Copied' }); }}>
 Copy
 </Button>
 </div>
 </div>
 </motion.div>
 );
 })}
 </div>
 )}

 <SettingsModal
 open={showForm}
 onClose={() => setShowForm(false)}
 title="Add Number Sequence"
 subtitle="Configure auto-numbering rules"
 icon={Hash}
 footer={
 <>
 <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
 <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate({ ...form, padding: parseInt(form.padding), nextNumber: parseInt(form.nextNumber) })}
 isLoading={saveMutation.isPending} disabled={!form.name || !form.code}>
 Save Sequence
 </Button>
 </>
 }
 >
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Invoice Numbers" required />
 <Input label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="INV" required />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <Input label="Prefix" value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} placeholder="INV-" />
 <Input label="Suffix" value={form.suffix} onChange={(e) => setForm({ ...form, suffix: e.target.value })} placeholder="/2024" />
 </div>
 <div className="grid grid-cols-3 gap-3">
 <Input label="Padding" type="number" value={form.padding} onChange={(e) => setForm({ ...form, padding: e.target.value })} />
 <Input label="Next Number" type="number" value={form.nextNumber} onChange={(e) => setForm({ ...form, nextNumber: e.target.value })} />
 <div>
 <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Reset</label>
 <select value={form.resetFrequency} onChange={(e) => setForm({ ...form, resetFrequency: e.target.value })}
 className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
 <option value="never">Never</option><option value="daily">Daily</option>
 <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
 </select>
 </div>
 </div>
 <div className="p-4 bg-slate-50 rounded-xl text-center">
 <p className="text-xs text-slate-500 mb-1">Preview</p>
 <p className="text-2xl font-bold font-mono text-slate-900 ">{generatePreview()}</p>
 </div>
 </div>
 </SettingsModal>
 </div>
 </div>
 );
}